import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { API } from '@/api'
import { message } from '@/components'
import { useUserStore } from '@/stores/user'

import {
  createDeleteClickHandler,
  createDomainChangeHandler,
  createFileClickHandler,
  createFileDeleteHandler,
  createMoveClickHandler,
  createRenameClickHandler,
  createRowSelectionHandler,
  createUploadHandler,
  usePathUtilities,
  useUploadingTasks,
} from '../core/hooks/file-manager-utils'
import { IFile } from '../fs'
import * as fs from '../fs'
import { useDelete } from '../useDelete'
import { useMove } from '../useMove'
import { useRename } from '../useRename'
import { useUpload } from '../useUpload'

interface UseFileManagerOptions {
  /**
   * Base path for routing (e.g., '/data' for platform, '/team/data/member' for team member data)
   */
  basePath: string
}

interface UseFileManagerReturn {
  // States
  files: IFile[]
  selectedRows: IFile[]
  loading: boolean
  domainInfos: any[]
  currentDomain: number
  currentDomainVolume: { quota_gb: number; usage_mb: number } | null
  volumes: any[]
  uploadingTasks: Array<{
    name: string
    size: number
    loaded?: number
    status: number
    speed?: number
  }>
  showMgrInstruction: boolean

  // Upload control
  uploadList: any[] // Full upload list for TransferStatusIndicator
  pauseTask: (task: any) => void
  resumeTask: (task: any) => void

  // Path utilities
  dirPath: string
  pathList: string[]
  getPathUrl: (path: string) => string

  // User store derived values
  hasService: boolean
  isOrderExpired: boolean
  notAvailable: boolean

  // Handlers
  handleRowSelection: (file: IFile, checked: boolean) => void
  handleSelectAll: (checked: boolean) => void
  handleFileClick: (file: IFile) => void
  onFileChange: (event: React.SyntheticEvent) => void
  onRowDeleteClick: (row: IFile) => void
  onRowRenameClick: (row: IFile) => void
  onRowMoveClick: (row: IFile) => void

  // Modal states
  deleteModalOpen: boolean
  setDeleteModalOpen: (open: boolean) => void
  renameInEdit: IFile | undefined
  setRenameInEdit: (file: IFile | undefined) => void
  moveInEdit: IFile[] | undefined
  setMoveInEdit: (files: IFile[] | undefined) => void

  // Actions
  onFileDeleteClick: () => Promise<void>
  fetchFiles: (path?: string, domain?: number) => Promise<boolean>
  onDomainChange: (value: number) => void

  // Dialog components
  RenameDialog: React.ComponentType<any>
  MoveDialog: React.ComponentType<any>

  // Translation function
  t: (key: string) => string
}

/**
 * Custom hook for managing file operations in datadrive
 * Encapsulates all file management logic including:
 * - File listing and navigation
 * - File selection and operations (upload, delete, rename, move)
 * - Domain management
 * - Upload quota checking
 * - Order expiration handling
 */
export function useFileManager(options: UseFileManagerOptions): UseFileManagerReturn {
  const { basePath } = options
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // States
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [renameInEdit, setRenameInEdit] = useState<IFile | undefined>()
  const [moveInEdit, setMoveInEdit] = useState<IFile[] | undefined>()
  const [selectedRows, setSelectedRows] = useState<IFile[]>([])
  const [files, setFiles] = useState<IFile[]>([])
  const { uploadList, uploadFile, successNum, pauseTask, resumeTask } = useUpload()
  const { deleteFiles } = useDelete()
  const [currentDomain, setCurrentDomain] = useState(0)
  const [currentDomainVolume, setCurrentDomainVolume] = useState<{
    quota_gb: number
    usage_mb: number
  } | null>(null)
  const [volumes, setVolumes] = useState<any[]>([])
  const prevDomainRef = useRef(currentDomain)
  const [loading, setLoading] = useState(false)
  const [domainInfos, setDomainInfos] = useState<any[]>([])
  const [showMgrInstruction, setShowMgrInstruction] = useState(false)

  // Derived values from user store (using selectors to avoid unnecessary re-renders)
  const hasService = useUserStore((state) => Boolean(state.domainCurrentServiceMap[currentDomain]))
  const isOrderExpired = useUserStore((state) => state.isOrderExpired)
  const notAvailable = !useUserStore((state) => state.partVolume?.quota_gb)

  // Path utilities (using shared hook)
  const { dirPath, pathList, getPathUrl } = usePathUtilities(basePath, searchParams)

  // Dialog components
  const { RenameDialog } = useRename(currentDomain)
  const { MoveDialog } = useMove(currentDomain)

  // Get available domains
  const getDomains = useCallback(async () => {
    setLoading(true)
    try {
      const [domainRes, volumesRes] = await Promise.all([API.getDomainList(), API.getVolumes()])

      if (domainRes.code === 0 && volumesRes.code === 0) {
        // Get current isOrderExpired state
        const { isOrderExpired } = useUserStore.getState()

        // Filter out domain 3(Japan) and domains with no volumes or zero quota
        const filteredDomainInfos = (domainRes.domainInfos || []).filter((info: any) => {
          if (info.domain === 3) return false

          const domainVolumes = volumesRes.volumes.filter(
            (volume: any) => volume.domain === info.domain,
          )

          if (isOrderExpired) return domainVolumes.length > 0

          // Check if domain has any volumes and if any volume has non-zero quota
          return (
            domainVolumes.length > 0 && domainVolumes.some((volume: any) => volume.quota_gb > 0)
          )
        })

        setDomainInfos(filteredDomainInfos)
        setVolumes(volumesRes.volumes)

        // Set currentDomain to the first available domain if there are any
        if (filteredDomainInfos.length > 0) {
          setCurrentDomain(filteredDomainInfos[0].domain)
          setCurrentDomainVolume(
            volumesRes.volumes.find(
              (volume: any) => volume.domain === filteredDomainInfos[0].domain,
            ) || null,
          )
          setShowMgrInstruction(false)
        } else {
          setShowMgrInstruction(true)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // Filter uploading tasks (using shared hook)
  const uploadingTasks = useUploadingTasks(uploadList, {
    domain: currentDomain,
    target: dirPath,
  })

  // Fetch files for current directory
  const fetchFiles = useCallback(
    async (p = dirPath, domain = currentDomain) => {
      if (!hasService) return false

      setLoading(true)
      try {
        const { code, msg, files } = await fs.ls({ path: p, domain })

        if (code === 0) {
          setSelectedRows([])
          setFiles(files)
          return true
        } else if (code === 15) {
          navigate(basePath)
        } else if (code === -4) {
          if (msg.startsWith('timeout of')) {
            message.error(t('common.errorMsg.fs.timeout'))
          } else if (msg === 'Network Error') {
            message.error(t('common.errorMsg.fs.networkError'))
          }
        } else {
          message.error(t('common.errorMsg.fs.getListFailed'))
        }
        return false
      } catch (error) {
        console.error(error)
        return false
      } finally {
        setLoading(false)
      }
    },
    [dirPath, currentDomain, hasService, basePath, t],
  )

  // Event handlers (using shared utilities)
  const handleRowSelection = createRowSelectionHandler(setSelectedRows)
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedRows(checked ? files : [])
    },
    [files],
  )
  const handleFileClick = createFileClickHandler(
    dirPath,
    selectedRows,
    getPathUrl,
    handleRowSelection,
    navigate,
  )
  const onRowDeleteClick = createDeleteClickHandler(setSelectedRows, setDeleteModalOpen)
  const onRowRenameClick = createRenameClickHandler(setRenameInEdit)
  const onRowMoveClick = createMoveClickHandler(setMoveInEdit)
  const onDomainChange = createDomainChangeHandler(
    volumes,
    setCurrentDomain,
    setCurrentDomainVolume,
  )

  // Upload handler (using shared utility)
  const onFileChange = createUploadHandler(
    currentDomainVolume,
    dirPath,
    currentDomain,
    uploadFile,
    t,
  )

  // Delete handler (using shared utility)
  // Wrap deleteFiles to match the expected signature (Platform has 3 params: files, dir, domain)
  const deleteFilesAdapter = useCallback(
    (files: IFile[], domain: number) => deleteFiles(files, dirPath, domain),
    [deleteFiles, dirPath],
  )
  const onFileDeleteClick = createFileDeleteHandler(
    selectedRows,
    dirPath,
    currentDomain,
    deleteFilesAdapter,
    fetchFiles,
    setDeleteModalOpen,
    setLoading,
    t,
  )

  // Effect: Handle domain switching
  useEffect(() => {
    if (prevDomainRef.current !== currentDomain) {
      navigate(getPathUrl('/'))
      fetchFiles('/', currentDomain)
      prevDomainRef.current = currentDomain
    } else {
      fetchFiles(dirPath, currentDomain)
    }
  }, [currentDomain, dirPath, successNum, navigate, getPathUrl, fetchFiles])

  // Effect: Fetch order status on mount
  useEffect(() => {
    useUserStore.getState().getOrder()
  }, [])

  // Effect: Get domains when order status is available
  useEffect(() => {
    if (isOrderExpired !== undefined) {
      getDomains()
    }
  }, [isOrderExpired, getDomains])

  return {
    // States
    files,
    selectedRows,
    loading,
    domainInfos,
    currentDomain,
    currentDomainVolume,
    volumes,
    uploadingTasks,
    showMgrInstruction,

    // Upload control
    uploadList,
    pauseTask,
    resumeTask,

    // Path utilities
    dirPath,
    pathList,
    getPathUrl,

    // User store derived values
    hasService,
    isOrderExpired,
    notAvailable,

    // Handlers
    handleRowSelection,
    handleSelectAll,
    handleFileClick,
    onFileChange,
    onRowDeleteClick,
    onRowRenameClick,
    onRowMoveClick,

    // Modal states
    deleteModalOpen,
    setDeleteModalOpen,
    renameInEdit,
    setRenameInEdit,
    moveInEdit,
    setMoveInEdit,

    // Actions
    onFileDeleteClick,
    fetchFiles,
    onDomainChange,

    // Dialog components
    RenameDialog,
    MoveDialog,

    // Translation function
    t,
  }
}
